import { UA, WebSocketInterface, debug } from "jssip";
import { UAConfiguration } from "jssip/lib/UA";

type EventHandler = (...args: any[]) => void;

export class JSSIPManager {
  private ua: UA | null = null;
  private handlers: Map<string, EventHandler[]> = new Map();
  private initializing = false;

  async initFromAuth(auth: { host: string; user: string; password: string }) {
    if (this.ua) return;
    if (this.initializing) return;
    this.initializing = true;

    const socket = new WebSocketInterface(`wss://${auth.host}`);

    const configuration: UAConfiguration = {
      sockets: [socket],
      uri: `sip:${auth.user}@${auth.host}`,
      password: auth.password,
      register: true,
      register_expires: 30,
      session_timers: false,
    };

    this.ua = new UA(configuration);
    this.attachUAHandlers();
    this.ua.start();
    this.initializing = false;
  }

  private attachUAHandlers() {
    if (!this.ua) return;

    this.ua.on("connected", () => this.emit("connected"));
    this.ua.on("disconnected", (e: any) => this.emit("disconnected", e));
    this.ua.on("newRTCSession", (data: any) =>
      this.emit("newRTCSession", data)
    );
    this.ua.on("registered", () => this.emit("registered"));
    this.ua.on("unregistered", () => this.emit("unregistered"));
    this.ua.on("registrationFailed", (e: any) =>
      this.emit("registrationFailed", e)
    );
  }

  on(event: string, handler: EventHandler) {
    const arr = this.handlers.get(event) ?? [];
    arr.push(handler);
    this.handlers.set(event, arr);
  }

  off(event: string, handler?: EventHandler) {
    if (!handler) {
      this.handlers.delete(event);
      return;
    }
    const arr = (this.handlers.get(event) ?? []).filter((h) => h !== handler);
    if (arr.length) this.handlers.set(event, arr);
    else this.handlers.delete(event);
  }

  private emit(event: string, ...args: any[]) {
    (this.handlers.get(event) ?? []).forEach((h) => {
      try {
        h(...args);
      } catch (e) {
        // ignore handler errors
        console.error("JSSIPManager handler error", e);
      }
    });
  }

  async stop() {
    try {
      this.ua?.unregister();
      this.ua?.removeAllListeners();
      this.ua?.terminateSessions();
      this.ua?.stop();
    } finally {
      this.ua = null;
      this.handlers.clear();
      this.initializing = false;
    }
  }

  getUA() {
    return this.ua;
  }
}

export const jssipManager = new JSSIPManager();
